import React from 'react';

/**
 * SafeMount — contains a render error to ONE subtree instead of the Canvas.
 *
 * The world loads assets that are produced by a pipeline, so "not there yet" is
 * a normal state, not a broken one: a roster entry can list a rigged mesh and a
 * walk clip that exist while its idle clip is still rendering. A missing GLB is
 * not a 404 to the loader either — the dev server answers unknown paths with
 * index.html, so GLTFLoader gets HTML, fails to parse it as JSON, and throws.
 *
 * Unwrapped, that single throw unmounts everything up to the Canvas: one
 * unfinished avatar took down the entire world and dropped the WebGL context.
 * Wrapped, the world renders without that one NPC.
 *
 * `retryMs` re-mounts the subtree on a timer, so an asset that finishes
 * rendering mid-session appears on its own rather than needing a reload.
 */
export default class SafeMount extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, attempt: 0 };
    this.timer = null;
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // Name it in the console — silence here would turn a missing asset into a
    // mystery ("why is that artist not in the street?").
    console.warn(`[SafeMount] ${this.props.label || 'subtree'} failed to render:`, error?.message || error);
    const retryMs = this.props.retryMs;
    if (retryMs && this.state.attempt < (this.props.maxRetries ?? 5)) {
      this.timer = setTimeout(() => {
        // The asset cache stores REJECTIONS as well as results, so a remount
        // alone would re-throw the same cached failure forever. onRetry is where
        // the owner evicts the failed URLs — without it, "retry" is cosmetic.
        try { this.props.onRetry?.(); } catch { /* eviction is best-effort */ }
        this.setState((s) => ({ failed: false, attempt: s.attempt + 1 }));
      }, retryMs);
    }
  }

  componentWillUnmount() {
    if (this.timer) clearTimeout(this.timer);
  }

  render() {
    if (this.state.failed) return this.props.fallback ?? null;
    // The attempt counter is the remount key: without it React reuses the
    // failed subtree's fibers and the retry renders nothing.
    return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
  }
}
